/**
 * ThemeToggle — the shared light/dark switch used in every page chrome
 * (app shell, auth, onboarding, admin, seller surfaces, and the landing top
 * bar). Pairs with `LanguageSwitcher` as a chrome "TopToggles" cluster.
 *
 * The UI store's `theme` may be `"light" | "dark" | "system"`. This control
 * resolves the *effective* theme (reading the OS preference for `"system"`,
 * the same rule `ThemeController` applies) so the glyph always reflects what
 * the user currently sees, and a click sets the *explicit* opposite theme via
 * `ui-store.setTheme`. From then on the choice is explicit (no longer tracking
 * the OS) — the expected behaviour for a manual toggle.
 *
 * The glyph shows the *target* mode (moon while currently light → "go dark";
 * sun while currently dark → "go light"). The accessible name comes from the
 * `common:theme.toggle` key (no hardcoded copy, available on every surface);
 * the icon is decorative. The button is a ≥44px tap target with a visible focus
 * ring; the theme is conveyed by both the icon and the label, never by colour
 * alone.
 *
 * Restyled 2026-08 (counter revamp): it was a filled `bg-card` disc with a
 * component-hairline border, which on the cream ground floated as a third
 * surface behind the header's controls. It now shares one family with the
 * language trigger and the search well — a drawn `--g-rule` border on a
 * transparent ground, filling to `--g-sunken` on hover — so the header's
 * right-hand cluster reads as one instrument panel instead of three unrelated
 * widgets. Geometry (`size-11`, the testids, the aria contract) is unchanged,
 * so every other surface that hosts it (auth, landing, admin, seller, 404)
 * keeps its layout. The glyph comes from lucide at the shared `iconProps`
 * stroke rather than at a per-call-site weight.
 */
import { useSyncExternalStore, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Moon, Sun } from "lucide-react";

import { iconProps } from "@/lib/icons";
import { useUiStore, type Theme } from "@/lib/ui-store";

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

function hasMatchMedia(): boolean {
  return (
    typeof window !== "undefined" && typeof window.matchMedia === "function"
  );
}

/** Subscribe to OS `prefers-color-scheme` so a `"system"` theme re-resolves
 *  when the OS flips. No-op subscription under jsdom (no `matchMedia`). */
function subscribePrefersDark(onChange: () => void): () => void {
  if (!hasMatchMedia()) return () => {};
  const mql = window.matchMedia(COLOR_SCHEME_QUERY);
  mql.addEventListener("change", onChange);
  return () => {
    mql.removeEventListener("change", onChange);
  };
}

function getPrefersDark(): boolean {
  return hasMatchMedia() && window.matchMedia(COLOR_SCHEME_QUERY).matches;
}

/** Resolve a (possibly `"system"`) preference to the concrete light/dark the
 *  user currently sees — mirrors `ThemeController.resolveTheme`. */
function resolveEffectiveTheme(
  theme: Theme,
  prefersDark: boolean,
): "light" | "dark" {
  if (theme !== "system") return theme;
  return prefersDark ? "dark" : "light";
}

export interface ThemeToggleProps {
  /** Extra classes for the button (e.g. alignment in a top bar). */
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps): ReactElement {
  const { t } = useTranslation("common");
  const theme = useUiStore((state) => state.theme);
  const setTheme = useUiStore((state) => state.setTheme);

  // `getPrefersDark` is stable; the snapshot is a primitive boolean, so
  // useSyncExternalStore won't loop.
  const prefersDark = useSyncExternalStore(
    subscribePrefersDark,
    getPrefersDark,
    () => false,
  );

  const effective = resolveEffectiveTheme(theme, prefersDark);
  const isDark = effective === "dark";
  const next: Theme = isDark ? "light" : "dark";

  return (
    <button
      type="button"
      onClick={() => {
        setTheme(next);
      }}
      aria-label={t("theme.toggle")}
      aria-pressed={isDark}
      data-testid="theme-toggle"
      data-theme-state={effective}
      className={[
        "inline-flex size-11 shrink-0 items-center justify-center rounded-[10px]",
        "border border-[color:var(--g-rule)] bg-transparent text-foreground",
        "transition-[transform,background-color] duration-150 ease-[var(--g-ease)]",
        "hover:bg-[color:var(--g-sunken)] active:scale-95 motion-reduce:transition-none motion-reduce:active:scale-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {isDark ? <Sun {...iconProps("md")} /> : <Moon {...iconProps("md")} />}
    </button>
  );
}

export default ThemeToggle;
